/**
 * Phase 2 weights — all 6 signals active.
 * If a signal's raw input was unavailable (score === 50 sentinel), its weight
 * is redistributed proportionally among the signals that had real data.
 */
import type { InferenceResult, MatchBand, DimensionScores } from '../../types';

const BASE_WEIGHTS: Record<keyof DimensionScores, number> = {
  skill_overlap:        0.30,
  semantic_similarity:  0.25,
  experience_alignment: 0.20,
  budget_fit:           0.10,
  location_fit:         0.10,
  deadline_feasibility: 0.05,
};

/** Sentinel: functions return 50 when input data is unavailable. */
const NEUTRAL = 50;

function bandFromScore(score: number): MatchBand {
  if (score >= 75) return 'strong_fit';
  if (score >= 55) return 'good_fit';
  if (score >= 35) return 'partial_fit';
  return 'not_recommended';
}

/**
 * Dimension key → human-readable label for reasoning sentences.
 * semantic_similarity is intentionally omitted — it's a vector distance,
 * not a concept the recruiter can act on.
 */
const DIM_LABEL: Partial<Record<keyof DimensionScores, string>> = {
  skill_overlap:        'skills',
  experience_alignment: 'experience',
  budget_fit:           'compensation',
  location_fit:         'location',
  deadline_feasibility: 'availability',
};

/**
 * Generates a per-candidate reasoning sentence that explains the match
 * band using the actual skills and dimension data.  This replaces the
 * static 4-phrase switch so every card shows a different, actionable
 * message rather than identical boilerplate.
 */
function generateReasoning(
  band: MatchBand,
  matched: string[],
  missing: string[],
  dimensionScores: DimensionScores,
): string {
  const topMatched = matched.slice(0, 3);
  const topMissing = missing.slice(0, 3);
  const matchStr = topMatched.length > 0 ? topMatched.join(', ') : null;
  const missStr  = topMissing.length > 0 ? topMissing.join(', ') : null;

  // Weakest non-neutral named dimension — used as fallback when skills lists
  // are empty.
  const weakest = (Object.entries(DIM_LABEL) as [keyof DimensionScores, string][])
    .map(([k, label]) => ({ label, score: dimensionScores[k] }))
    .filter((d) => d.score !== NEUTRAL)
    .sort((a, b) => a.score - b.score)[0];

  switch (band) {
    case 'strong_fit':
      if (matchStr && missStr)
        return `Excellent match — covers ${matchStr}; minor gap in ${missStr}.`;
      if (matchStr)
        return `Excellent match — covers ${matchStr}. All key requirements met.`;
      return 'Strong overall fit — recommend to proceed.';

    case 'good_fit':
      if (matchStr && missStr)
        return `Good fit — strong on ${matchStr}; could strengthen ${missStr}.`;
      if (matchStr)
        return `Good fit — covers ${matchStr}. Worth considering.`;
      return 'Good fit — recommend for review.';

    case 'partial_fit':
      if (matchStr && missStr)
        return `Partial match — has ${matchStr} but missing ${missStr}.`;
      if (missStr)
        return `Partial match — gaps in ${missStr}. Review alignment before proceeding.`;
      if (weakest)
        return `Partial fit — ${weakest.label} alignment needs review.`;
      return 'Partial fit — review gaps before proceeding.';

    case 'not_recommended':
      if (matched.length === 0 && missStr)
        return `Low fit — missing key requirements: ${missStr}.`;
      if (missStr && matchStr)
        return `Not recommended — critical gaps in ${missStr}, despite having ${matchStr}.`;
      if (missStr)
        return `Not recommended — missing ${missStr}.`;
      if (weakest && weakest.score < 35)
        return `Not recommended — ${weakest.label} significantly below the required bar.`;
      return 'Not recommended — significant misalignment with role requirements.';
  }
}

/**
 * Build effective weights: signals that returned the neutral 50 sentinel
 * (meaning their inputs were unavailable) have their weight redistributed
 * proportionally to the signals that did have real data.
 */
function effectiveWeights(
  scores: DimensionScores,
  available: Record<keyof DimensionScores, boolean>
): Record<keyof DimensionScores, number> {
  const keys = Object.keys(BASE_WEIGHTS) as (keyof DimensionScores)[];

  const totalAvailableWeight = keys.reduce(
    (sum, k) => sum + (available[k] ? BASE_WEIGHTS[k] : 0),
    0
  );

  if (totalAvailableWeight === 0) {
    // All signals unavailable — fall back to equal weight on all
    const eq = 1 / keys.length;
    return Object.fromEntries(keys.map((k) => [k, eq])) as Record<keyof DimensionScores, number>;
  }

  return Object.fromEntries(
    keys.map((k) => [k, available[k] ? BASE_WEIGHTS[k] / totalAvailableWeight : 0])
  ) as Record<keyof DimensionScores, number>;
}

export function runInference(
  skillOverlapResult: { score: number; matched: string[]; missing: string[]; bonus: string[] },
  semanticScore: number,
  expScore: number,
  budgetScoreVal: number,
  locationScoreVal: number,
  deadlineScoreVal: number
): InferenceResult {
  const dimensionScores: DimensionScores = {
    skill_overlap:        skillOverlapResult.score,
    semantic_similarity:  semanticScore,
    experience_alignment: expScore,
    budget_fit:           budgetScoreVal,
    location_fit:         locationScoreVal,
    deadline_feasibility: deadlineScoreVal,
  };

  // A score of exactly NEUTRAL (50) from a stub/unavailable input should not
  // be treated as real data — we redistribute weight from those signals.
  // We treat skill & semantic as always "available" (they always have real data).
  const available: Record<keyof DimensionScores, boolean> = {
    skill_overlap:        true,
    semantic_similarity:  true,
    experience_alignment: expScore !== NEUTRAL,
    budget_fit:           budgetScoreVal !== NEUTRAL,
    location_fit:         locationScoreVal !== NEUTRAL,
    deadline_feasibility: deadlineScoreVal !== NEUTRAL,
  };

  const weights = effectiveWeights(dimensionScores, available);

  let weightedSum = 0;
  for (const key of Object.keys(weights) as (keyof DimensionScores)[]) {
    weightedSum += dimensionScores[key] * weights[key];
  }

  const compositeScore = Math.round(Math.max(0, Math.min(100, weightedSum)));
  const band = bandFromScore(compositeScore);

  return {
    compositeScore,
    dimensionScores,
    matched: skillOverlapResult.matched,
    missing: skillOverlapResult.missing,
    bonus: skillOverlapResult.bonus,
    band,
    recommendation: generateReasoning(band, skillOverlapResult.matched, skillOverlapResult.missing, dimensionScores),
  };
}
